import type { FC } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import ArrowRight from "@/design-system/icons/arrow-right";
import { sendTelemetryEvent } from "@/lib/telemetry";
import {
    CashbackDetailsResources,
    type ICashbackDetailsResources,
} from "@/src/cashback/CashbackDetails/CashbackDetails.Resources";
import { CashbackDetailsResourcesEn } from "@/src/cashback/CashbackDetails/CashbackDetails.Resources.En";
import RoundTile from "@/src/common/RoundTile";
import ClockSmall from "@/src/common/icons/ClockSmall";
import { languageSelector } from "@/src/contexts";
import * as stylex from "@stylexjs/stylex";

import {
    ComposableSpacing,
    type QueryMdMax,
    type QueryXsMax,
    colors,
    genericStyles,
} from "../../../styles/tokens.stylex";
import type { ICashbackBrandTile } from "../CashbackCommonInterfaces";

import * as css from "./SpecialCashbackBrands.module.css";

export interface ISpecialCashbackBrands {
    cashbackBrands?: ICashbackBrandTile[];
}

const SpecialCashbackBrands: FC<ISpecialCashbackBrands> = (props) => {
    const resources = languageSelector(CashbackDetailsResources, CashbackDetailsResourcesEn);
    const navigate = useNavigate();

    const handleNavigation = (ev: React.MouseEvent, url: string, id: string) => {
        ev.preventDefault();
        ev.stopPropagation();

        sendTelemetryEvent({
            eventName: "cshbck-brand",
            itemId: id,
            actionType: "Click",
        });

        navigate(url);
    };

    return (
        <div className={css.sectionCashbackBrandsComponent}>
            {props.cashbackBrands?.map((item) => (
                <CashbackBrandTile
                    key={item.id}
                    item={item}
                    resources={resources}
                    handleNavigation={handleNavigation}
                />
            ))}
        </div>
    );
};

const CashbackBrandTile: FC<{
    item: ICashbackBrandTile;
    resources: ICashbackDetailsResources;
    handleNavigation: (ev: React.MouseEvent, url: string, id: string) => void;
}> = ({ item, resources, handleNavigation }) => {
    const [isHovered, setIsHovered] = useState(false);
    const isSpecialOffer = item.href && !!item.specialCashback;

    const renderExpireTime = (timeLimit: string | undefined) => (
        <div {...stylex.props(styles.expireDate)}>
            <ClockSmall styleX={styles.expireImg} />
            <span {...stylex.props(styles.expireDescription)}>
                {resources.specialCashbackTimeLimitText}
                <b {...stylex.props(styles.expireDescriptionBold)}>{timeLimit}</b>
            </span>
        </div>
    );

    return (
        <div
            key={item.id}
            {...stylex.props(styles.cashbackRoundTile, isSpecialOffer && styles.specialCashback)}
            onClick={(ev) => handleNavigation(ev, item.href, item.id)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {isSpecialOffer && (
                <div {...stylex.props(styles.superLabel)}>{resources.specialCashbackBadgeTitle}</div>
            )}

            <RoundTile
                id={item.id}
                image={item.image}
                description={item.description}
                subDescription={item.subDescription}
                secondSubDescription={item.secondSubDescription}
                specialCashback={item.specialCashback}
                telemetryCustom={{
                    eventName: "cshbck-brand",
                    itemId: item.id,
                    actionType: "Click",
                }}
                href={item.href}
                styleX={[styles.items]}
                favoriteLinkStyleX={styles.heartIcon}
                hideFlameIcon
                showSave
            />

            {isSpecialOffer && renderExpireTime(item.specialCashbackTimeLimit)}
            <span {...stylex.props(styles.arrowNext, isHovered && styles.arrowHover)}>
                <ArrowRight />
            </span>
        </div>
    );
};

const xsMax: QueryXsMax = "@media (max-width: 759px)";
const mdMax: QueryMdMax = "@media (max-width: 1239px)";

const styles = stylex.create({
    items: {
        paddingBottom: 15,
        paddingLeft: { default: 40, [mdMax]: 20 },
        paddingRight: { default: 40, [mdMax]: 20 },
        paddingTop: { default: 25, [mdMax]: 10 },
    },
    heartIcon: {
        right: { default: 15, [xsMax]: 10 },
        top: { default: 15, [xsMax]: 10 },
    },

    cashbackRoundTile: {
        borderColor: colors.b_centas,
        borderRadius: ComposableSpacing.s3,
        borderStyle: "solid",
        borderWidth: 1,
        backgroundColor: colors.bg_Primary,
        boxShadow: {
            default: null,
            [xsMax]: "0px 3px 15px rgba(0, 0, 0, 0.2)",
            ":hover": "0px 3px 15px rgba(0, 0, 0, 0.2)",
        },
        cursor: "pointer",
        position: "relative",
        width: "100%",
    },

    specialCashback: {
        borderColor: colors.b_BrandMagenta,
        borderStyle: "solid",
        borderWidth: 2,
    },

    superLabel: {
        borderRadius: genericStyles.borderRadiusFullRound,
        backgroundColor: colors.b_BrandMagenta,
        color: "white",
        fontSize: "0.6875rem",

        fontWeight: "bold",
        letterSpacing: "0.3px",

        position: "absolute",
        textTransform: "uppercase",

        zIndex: 10,
        left: { default: 20, [xsMax]: 10 },
        paddingBottom: 6,
        paddingLeft: 11,
        paddingRight: 11,
        paddingTop: 6,
        top: { default: 15, [xsMax]: 12 },
    },

    expireDate: {
        alignItems: "center",

        color: colors.grayLight,
        display: "flex",
        fontSize: ComposableSpacing.s3,

        letterSpacing: 0.3,
        borderTopColor: colors.fg_Disabled,
        borderTopStyle: "solid",
        borderTopWidth: 1,
        paddingBottom: { default: 12, [xsMax]: 10 },
        paddingLeft: { default: 15, [xsMax]: 10 },
        paddingRight: { default: 15, [xsMax]: 10 },
        paddingTop: { default: 12, [xsMax]: 10 },
    },

    expireImg: {
        height: 18,
        width: 18,
    },

    expireDescription: {
        marginLeft: 10,
    },

    expireDescriptionBold: {
        display: "block",
    },

    arrowNext: {
        strokeWidth: 3,
        color: colors.fg_BrandMagenta,
        opacity: 0,
        position: "absolute",
        transitionDuration: "600ms",
        transitionProperty: "all",
        bottom: 10,
        height: 0,
        transitionTimingFunction: "ease",
        right: 15,
        width: 0,
    },
    arrowHover: {
        opacity: 1,
        height: 28,
        width: 28,
    },
});

export default SpecialCashbackBrands;
